import { useCallback, useEffect, useState } from 'react';
import {
  exportFundingReport,
  fetchFundingReportCommissions,
  fetchFundingReportLenders,
  fetchFundingReportPipeline,
  fetchFundingReportReadiness,
  fetchFundingReportSummary,
} from '../../services/funding/fundingReportsService';

const FETCHERS = {
  summary: fetchFundingReportSummary,
  pipeline: fetchFundingReportPipeline,
  commissions: fetchFundingReportCommissions,
  readiness: fetchFundingReportReadiness,
  lenders: fetchFundingReportLenders,
};

export function useFundingReports({ tab = 'summary', filters = {}, autoLoad = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (targetTab = tab) => {
    const fetcher = FETCHERS[targetTab];
    if (!fetcher) return null;

    setLoading(true);
    setError(null);
    try {
      const result = await fetcher(filters);
      setData(result);
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to load report');
      setData(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [tab, filters]);

  const exportReport = useCallback(async (reportType = tab) => {
    setExporting(true);
    setError(null);
    try {
      return await exportFundingReport(reportType, filters);
    } catch (err) {
      setError(err?.message || 'Failed to export report');
      throw err;
    } finally {
      setExporting(false);
    }
  }, [tab, filters]);

  useEffect(() => {
    if (autoLoad) load(tab);
  }, [autoLoad, load, tab]);

  return {
    data,
    loading,
    exporting,
    error,
    load,
    exportReport,
    setData,
  };
}
